import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { EnrolmentTokenSummary } from '../lib/types';
import { useResource } from '../lib/useResource';
import { useSession } from '../lib/session';
import { Card, Empty, Loading, Notice, Pill, exactly, when } from '../components/ui';

interface IssuedToken {
  id: string;
  assetTag: string;
  enrolmentCode: string;
  expiresAt: string;
}

export function Enrolment() {
  const { can } = useSession();
  const [assetTag, setAssetTag] = useState('');
  const [note, setNote] = useState('');
  const [issued, setIssued] = useState<IssuedToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tokens = useResource(
    () => api.get<EnrolmentTokenSummary[]>('/api/v1/devices/enrolment-tokens'),
    [],
  );

  async function issue() {
    setBusy(true);
    setError(null);
    setIssued(null);
    try {
      const token = await api.post<IssuedToken>('/api/v1/devices/enrolment-tokens', {
        assetTag: assetTag.trim().toUpperCase(),
        note: note.trim() || undefined,
      });
      setIssued(token);
      setAssetTag('');
      setNote('');
      tokens.reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The code could not be issued');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    try {
      await api.delete(`/api/v1/devices/enrolment-tokens/${id}`);
      tokens.reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The code could not be revoked');
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Enrolment</h1>
        <p>
          A device can only join the platform with a code issued here against a known asset tag.
          That is what keeps personal machines, which are out of scope, off the fleet.
        </p>
      </div>

      {error && <Notice kind="error">{error}</Notice>}

      {issued && (
        <Notice kind="ok">
          <div>
            Code for <strong>{issued.assetTag}</strong>, valid until {exactly(issued.expiresAt)}:
          </div>
          <div
            className="mono"
            style={{ fontSize: 24, letterSpacing: '0.06em', margin: '8px 0 4px', fontWeight: 700 }}
          >
            {issued.enrolmentCode}
          </div>
          <div>
            This is shown once and cannot be retrieved again. Give it to the person collecting the
            device, or pass it to the deployment tool.
          </div>
        </Notice>
      )}

      {can('TECH_ADMIN') && (
        <Card title="Issue an enrolment code">
          <div className="filters">
            <label className="field">
              <span>
                Asset tag
                <span className="hint">Must match the label on the device</span>
              </span>
              <input
                type="text"
                value={assetTag}
                placeholder="RFS-LT-0000"
                onChange={(event) => setAssetTag(event.target.value.toUpperCase())}
              />
            </label>
            <label className="field grow">
              <span>Note</span>
              <input
                type="text"
                value={note}
                placeholder="Who it is for, or why it is being issued"
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
            <button type="button" disabled={busy || assetTag.trim().length < 3} onClick={issue}>
              {busy ? 'Issuing…' : 'Issue code'}
            </button>
          </div>
        </Card>
      )}

      <Card title="Outstanding codes" extra={tokens.data ? `${tokens.data.length}` : undefined}>
        {tokens.loading && !tokens.data && <Loading />}
        {tokens.error && <Notice kind="error">{tokens.error}</Notice>}
        {tokens.data?.length === 0 && <Empty>No enrolment codes are outstanding.</Empty>}

        {tokens.data && tokens.data.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr><th>Asset tag</th><th>Note</th><th>Issued</th><th>Expires</th><th>State</th><th></th></tr>
              </thead>
              <tbody>
                {tokens.data.map((token) => {
                  const expired = new Date(token.expiresAt).getTime() < Date.now();
                  return (
                    <tr key={token.id}>
                      <td><strong>{token.assetTag}</strong></td>
                      <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{token.note ?? '—'}</td>
                      <td>{when(token.createdAt)}</td>
                      <td title={exactly(token.expiresAt)}>{when(token.expiresAt)}</td>
                      <td>
                        <Pill tone={expired ? 'stop' : 'info'}>{expired ? 'expired' : 'awaiting use'}</Pill>
                      </td>
                      <td>
                        {can('TECH_ADMIN') && (
                          <button type="button" className="secondary small" onClick={() => revoke(token.id)}>
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
